// src/views/SpinAndWin.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    useTheme,
    Dialog,
    DialogContent,
    DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Confetti from "react-confetti";

const prizes = [1000, 2000, 5000, 10000, 20000, 50000];

const SpinAndWin: React.FC = () => {
    const [spinning, setSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [prize, setPrize] = useState<number | null>(null);
    const [showResult, setShowResult] = useState(false);
    const [spinsLeft, setSpinsLeft] = useState(1);
    const [mounted, setMounted] = useState(false);

    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        setMounted(true);
    }, []);

    const spin = () => {
        if (spinning || spinsLeft <= 0) return;

        setSpinning(true);
        const selectedPrize = prizes[Math.floor(Math.random() * prizes.length)];

        const extraSpins = 5;
        const finalRotation =
            rotation + extraSpins * 360 + Math.floor(Math.random() * 360);

        setRotation(finalRotation);

        setTimeout(() => {
            setPrize(selectedPrize);
            setShowResult(true);
            setSpinning(false);
            setSpinsLeft((prev) => prev - 1);
        }, 4000);
    };

    return (
        <Box
            sx={{
                px: 2,
                py: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minHeight: "100vh",
                bgcolor:
                    theme.palette.mode === "dark" ? theme.palette.grey[900] : "#f5f7fa",
            }}
        >
            {/* Back Button */}
            <Box sx={{ alignSelf: "flex-start", mb: 2 }}>
                <IconButton onClick={() => navigate("/rewards")}>
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            {mounted && prize !== null && (
                <Confetti recycle={false} numberOfPieces={300} />
            )}

            {/* Main Card */}
            <Card
                sx={{
                    maxWidth: 400,
                    width: "100%",
                    borderRadius: 4,
                    boxShadow: 6,
                    mb: 4,
                    textAlign: "center",
                    p: 3,
                }}
            >
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Spin & Win 🎡
                    </Typography>

                    <Typography variant="body1" color="text.secondary" mb={3}>
                        Spin the wheel and try your luck to win exciting cash rewards!
                    </Typography>

                    {/* Wheel */}
                    <Box
                        sx={{
                            mx: "auto",
                            mb: 3,
                            width: 250,
                            height: 250,
                            borderRadius: "50%",
                            border: "6px solid #444",
                            overflow: "hidden",
                            transform: `rotate(${rotation}deg)`,
                            transition: spinning
                                ? "transform 4s cubic-bezier(0.33, 1, 0.68, 1)"
                                : undefined,
                            background: `conic-gradient(
                                #f44336 0deg 60deg,
                                #2196f3 60deg 120deg,
                                #4caf50 120deg 180deg,
                                #ff9800 180deg 240deg,
                                #9c27b0 240deg 300deg,
                                #00bcd4 300deg 360deg
                            )`,
                        }}
                    />

                    {/* Spin Button */}
                    <Button
                        variant="contained"
                        size="large"
                        disabled={spinning || spinsLeft <= 0}
                        onClick={spin}
                        sx={{
                            borderRadius: "50px",
                            px: 4,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: "bold",
                        }}
                    >
                        {spinsLeft > 0 ? "Spin" : "No Spins Left"}
                    </Button>
                </CardContent>
            </Card>

            <Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    mt={2}
                >
                    Spin once daily for a chance to win up to ₦50,000!
                </Typography>
            </Box>

            {/* Prize Modal */}
            <Dialog open={showResult} onClose={() => setShowResult(false)}>
                <DialogContent sx={{ textAlign: "center" }}>
                    <Typography variant="h5" gutterBottom>
                        🎉 Congratulations!
                    </Typography>
                    {prize !== null && (
                        <Typography variant="h5" sx={{ mt: 2 }}>
                            You won ₦{prize.toLocaleString()}!
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        onClick={() => setShowResult(false)}
                        sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#388e3c" } }}
                    >
                        Claim
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SpinAndWin;
